import { createReducer } from '@reduxjs/toolkit';
import { ApiActionStatus, type Hotel, type SearchResultsState } from '@/interfaces';
import { getValidPropertiesFromObject } from '@/utils/model';

import { clearSearchResults, resetFetchStatus, setSearchResults, setSearchResultsError, setSearchResultsPending } from './actions';

export const initialState = {
    status: ApiActionStatus.NOT_STARTED,
    attempts: 0,
    isComplete: false,
    results: [],
    starRatings: [],
    guestRatings: [],
    chains: [],
    facilities: [],
    propertyTypes: [],
    providers: [],
};

const MAX_FETCH_ATTEMPTS = 8;
const ALLOWED_HOTEL_PROPERTIES = new Set([
    'id',
    'key',
    'name',
    'images',
    'starRating',
    'guestRating',
    'guestRatingSentiment',
    'numberOfReviews',
    'rates',
    'wellKnownPlace',
    'latitude',
    'longitude',
]);
const createHotel = (partialHotel: Partial<Hotel>): Hotel => getValidPropertiesFromObject(partialHotel, ALLOWED_HOTEL_PROPERTIES) as Hotel;

export const reducer = createReducer<SearchResultsState>(initialState, (builder) => {
    builder
        .addCase(resetFetchStatus, (state) => {
            state.isComplete = false;
            state.attempts = 0;
        })
        .addCase(clearSearchResults, (state, action) => {
            state.results = [];
            state.attempts = 0;
            state.currentFilters = action.payload;
        })
        .addCase(setSearchResultsPending, (state) => {
            state.status = ApiActionStatus.PENDING;
            state.attempts = state.attempts + 1;
        })
        .addCase(setSearchResults, (state, action) => {
            const { data, queryParams } = action?.payload || {
                data: undefined,
                searchConfig: undefined,
                queryParams: undefined,
            };

            if (!Array.isArray(data?.results) || queryParams !== state.currentFilters) {
                return state;
            }

            return {
                ...initialState,
                ...data,
                searchConfig: data.searchConfig ?? state.searchConfig,
                currentFilters: state.currentFilters,
                status: ApiActionStatus.OK,
                results: data.results.map(createHotel),
                attempts: state.attempts,
                isComplete: data.isComplete || state.attempts >= MAX_FETCH_ATTEMPTS,
            };
        })
        .addCase(setSearchResultsError, (state, action) => {
            if (action.payload !== state.currentFilters) {
                return;
            }

            state.isComplete = true;
            state.status = ApiActionStatus.ERROR;
        });
});
